import { useEffect, useState } from 'react';
import { Dialog, TextInputField, Button, SelectMenu, TextareaField, TrashIcon, ConfirmIcon, Pane, FormField } from 'evergreen-ui';
import { toLocalSQLDatetime, formatTime } from '../utils/timeUtils';

const AppointmentDialog = ({ isShown, onClose, onSave, onDelete, entry = null, guests = [], canEdit, userId, selectedDate = null }) => {
  const [form, setForm] = useState({ testo: '', guest_id: '', start_time: '', end_time: '' });
  const [startInput, setStartInput] = useState('');
  const [endInput, setEndInput] = useState('');
  const [isNarrow, setIsNarrow] = useState(typeof window !== 'undefined' ? window.innerWidth < 560 : false);

  const parseTimeInputToISO = (dateLike, timeStr) => {
    if (!timeStr) return '';
    const parts = String(timeStr).split(':');
    const hh = parseInt(parts[0] || '0', 10);
    const mm = parseInt(parts[1] || '0', 10);
    const base = dateLike ? new Date(dateLike) : (selectedDate ? new Date(selectedDate) : new Date());
    if (isNaN(base.getTime())) {
      const now = new Date();
      return new Date(now.getFullYear(), now.getMonth(), now.getDate(), hh, mm, 0, 0).toISOString();
    }
    return new Date(base.getFullYear(), base.getMonth(), base.getDate(), hh, mm, 0, 0).toISOString();
  };

  useEffect(() => {
    if (entry) {
      const testo = entry.data ? (entry.data.testo || '') : (entry.testo || '');
      const guest_id = entry.data && entry.data.guest_id ? String(entry.data.guest_id) : (entry.guest_id ? String(entry.guest_id) : '');
      const startIso = entry.start ? entry.start : (entry.start_time ? entry.start_time : '');
      const endIso = entry.end ? entry.end : (entry.end_time ? entry.end_time : '');

      const deriveInput = (val) => {
        if (!val) return '';
        if (typeof val === 'string' && val.indexOf('T') === -1 && val.split(':').length === 2) return val;
        return formatTime(val) || '';
      };

      setForm({ testo, guest_id, start_time: startIso || '', end_time: endIso || '' });
      setStartInput(deriveInput(startIso));
      setEndInput(deriveInput(endIso));
    } else {
      setForm({ testo: '', guest_id: '', start_time: '', end_time: '' });
      setStartInput('');
      setEndInput('');
    }
  }, [entry, isShown, selectedDate]);

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 560);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const isExistingEntry = !!(entry && ((entry.data && entry.data.id) || entry.id));

  const handleSave = () => {
    const payload = {
      user_id: userId,
      testo: form.testo,
      date: form.start_time ? toLocalSQLDatetime(form.start_time) : undefined,
      start_time: form.start_time ? toLocalSQLDatetime(form.start_time) : undefined,
      end_time: form.end_time ? toLocalSQLDatetime(form.end_time) : undefined,
      guest_id: form.guest_id === '' ? null : form.guest_id,
    };

    const entryId = entry && (entry.data && entry.data.id ? entry.data.id : (entry.id ? entry.id : null));
    if (entryId) {
      payload.id = entryId;
    } else {
      payload.entry_type = 'appuntamento';
    }

    const maybePromise = onSave && onSave(payload);
    return maybePromise;
  };

  const handleDelete = () => {
    if (!entry) return;
    const id = entry.type === 'entry' && entry.data ? entry.data.id : (entry.id || null);
    if (id && onDelete) {
      return onDelete(id);
    }
  };

  return (
    <Dialog
      isShown={isShown}
      title={isExistingEntry ? 'Modifica appuntamento' : 'Nuovo appuntamento'}
      onCloseComplete={onClose}
      hasFooter={false}
      shouldAutoFocus={false}
    >
      {entry && (entry.data && (entry.data.nome || entry.data.cognome)) && (
        <TextInputField label="Autore" value={`${entry.data.nome || ''} ${entry.data.cognome || ''}`.trim()} readOnly />
      )}

      <FormField label="Ospite coinvolto">
        <SelectMenu
          title="Ospite coinvolto"
          hasFilter
          options={[...(guests || []).map(g => ({ label: `${g.nome} ${g.cognome}`, value: String(g.id) })), { label: 'Tutti', value: 'tutti' } ]}
          selected={form.guest_id ? [form.guest_id] : []}
          onSelect={(item) => setForm(f => ({ ...f, guest_id: item.value }))}
        >
          <Button style={{ maxWidth: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {form.guest_id ? ((guests || []).find(g => String(g.id) === form.guest_id) ? `${(guests || []).find(g => String(g.id) === form.guest_id).nome} ${(guests || []).find(g => String(g.id) === form.guest_id).cognome}` : 'Tutti') : 'Seleziona ospite'}
          </Button>
        </SelectMenu>
      </FormField>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
        <div style={{ flex: '1 1 160px', minWidth: 140 }}>
          <TextInputField
            label="Ora di inizio"
            value={startInput || ''}
            inputProps={{ type: 'time' }}
            onChange={(e) => {
              const hhmm = e.target.value;
              setStartInput(hhmm);
              if (!hhmm) {
                setForm(f => ({ ...f, start_time: '' }));
                return;
              }
              const refDate = form.start_time || selectedDate || undefined;
              const iso = parseTimeInputToISO(refDate, hhmm);
              setForm(f => ({ ...f, start_time: iso }));
            }}
          />
        </div>

        <div style={{ flex: '1 1 160px', minWidth: 140 }}>
          <TextInputField
            label="Ora di fine"
            value={endInput || ''}
            inputProps={{ type: 'time' }}
            onChange={(e) => {
              const hhmm = e.target.value;
              setEndInput(hhmm);
              if (!hhmm) {
                setForm(f => ({ ...f, end_time: '' }));
                return;
              }
              const ref = form.end_time ? form.end_time : form.start_time;
              const iso = parseTimeInputToISO(ref, hhmm);
              setForm(f => ({ ...f, end_time: iso }));
            }}
          />
        </div>
      </div>

      <TextareaField label="Descrizione appuntamento" marginTop={8} value={form.testo} onChange={(e) => setForm(f => ({ ...f, testo: e.target.value }))} inputHeight={160} flexibleHeight />

      <Pane display="flex" gap={8} marginTop={8} justifyContent="flex-end" style={{ flexDirection: isNarrow ? 'column' : 'row' }}>
        {isExistingEntry && (
          <Button intent="danger" onClick={handleDelete} style={{ width: isNarrow ? '100%' : 'auto' }}>
            Elimina appuntamento <TrashIcon marginLeft={8} />
          </Button>
        )}
        <Button appearance="primary" intent="success" onClick={handleSave} style={{ width: isNarrow ? '100%' : 'auto' }}>
          {isExistingEntry ? 'Conferma modifica' : 'Aggiungi appuntamento'} <ConfirmIcon marginLeft={8} />
        </Button>
      </Pane>
    </Dialog>
  );
};

export default AppointmentDialog;
